import {Box, HStack, Spinner, Text, Tooltip, VStack} from '@chakra-ui/react';
import {useState} from 'react';
import {useCommand} from '@synako/haligator';
import {ItemCatalogue2StateFF} from 'ff-common';
import {State} from '@synako/halitrek';
import {
  ArticlesCadencierSuggestionsAjoutesEventDesc,
  LigneDePanierDesc,
  SuggestionsStateDesc,
} from 'api-types/ffconnect-hal';
import {useTranslation} from 'react-i18next';
import {useSimpleTextToast} from '@/hooks/useSimpleTextToast';
import {useLivraisonsPossiblesContext} from 'ff-common';
import {useNavigate} from 'react-router-dom';
import {PanierLink} from '../panier/controllers/PanierLink';
import {EtoilesIcon} from '@/components/svg/EtoilesIcon';
import {useAjouterAuPanier} from '@/hooks/livraison/use-ajouter-au-panier';
import {useCapture} from '@/CaptureSetup';

// Affichage des suggestions de quantité pour un article
// Bouton pour ajouter l'article avec sa suggestion de quantité
export const SuggestionQuantite: React.FC<{
  itemState: ItemCatalogue2StateFF;
  suggestionsState?: State<SuggestionsStateDesc>;
  lastSuggestionEvent?: State<ArticlesCadencierSuggestionsAjoutesEventDesc>;
  ligneDePanier?: State<LigneDePanierDesc>;
  libelle?: string; // Utilisé pour le tracking
}> = ({
  itemState,
  suggestionsState,
  lastSuggestionEvent,
  ligneDePanier,
  libelle,
}) => {
  const {t} = useTranslation();
  const textToast = useSimpleTextToast();
  const code_article = itemState.data.code;
  const {livraisonSelectionnee} = useLivraisonsPossiblesContext();
  const navigate = useNavigate();
  const capture = useCapture();

  // Tracking local du clic explicite sur la suggestion
  // Permet de distinguer un ajout via le bouton de suggestion d'un ajout manuel
  const [suggestionCliquee, setSuggestionCliquee] = useState(false);

  // Pour le changement de quantité si article deja présent au panier
  const {submit: submitChangerQuantite, isSubmitting: isChangementQuantite} =
    useCommand(ligneDePanier, 'c:changer-la-quantite');

  // Pour l'ajout au panier si article non présent au panier
  const {submit: ajouterAuPanierAction, isSubmitting: isAjoutAuPanier} =
    useAjouterAuPanier(livraisonSelectionnee.data.id, textToast);

  // Vérification si cet article fait partie des derniers ajoutés
  const articlesAjoutes =
    lastSuggestionEvent?.data.data.panier.articles_ajoutes;
  const estDansArticlesAjoutes =
    Array.isArray(articlesAjoutes) &&
    articlesAjoutes.some((article) => article.code_article === code_article);

  // Trouver la suggestion de quantité pour cet article
  const suggestions = suggestionsState?.data.suggestions;
  const suggestionArticle = Array.isArray(suggestions)
    ? suggestions.find((s) => s.code_article === code_article)
    : undefined;

  if (!suggestionArticle) return null;

  // L'article est considéré "suggestion appliquée" si :
  // - il a été ajouté via l'action groupée (lastSuggestionEvent)
  // - OU l'utilisateur a cliqué explicitement sur ce bouton de suggestion
  // (un ajout manuel à la même quantité ne déclenche pas l'état "appliquée")
  const suggestionAppliquee = estDansArticlesAjoutes || suggestionCliquee;

  const handleClick = async () => {
    setSuggestionCliquee(true);
    if (ligneDePanier) {
      // Si l'article est déjà dans le panier, on met à jour la quantité
      submitChangerQuantite({
        quantite: suggestionArticle.suggestion_quantite,
      });
      textToast(
        [
          t('cadencier.suggestion.article_changement_quantite'),
          <PanierLink
            key='voir-panier-cta'
            t={t}
            date_de_livraison={
              livraisonSelectionnee.data.tolede.date_de_livraison
            }
            navigate={navigate}
          />,
        ],
        {id: 'modifie-quantite-panier-toast'},
      );
    } else {
      // Sinon on ajoute l'article au panier
      ajouterAuPanierAction({
        code_article: code_article,
        quantite: suggestionArticle.suggestion_quantite,
        commande_en: 'unitaire',
      });
    }

    capture({
      name: 'cadencier:suggestion_quantite',
      props: {
        code_article,
        libelle,
        quantite_initiale: ligneDePanier?.data.quantite ?? 0,
        quantite_suggeree: suggestionArticle.suggestion_quantite,
        type_suggestion: 'individuelle',
      },
    });
  };

  const isSubmitting = isChangementQuantite || isAjoutAuPanier;

  return (
    <HStack
      spacing={2}
      alignItems='center'
      justifyContent='center'
      border='1px solid'
      borderColor='gray.light'
      borderRadius='5px'
      background={!suggestionAppliquee ? 'white' : undefined}
      bgGradient={
        suggestionAppliquee ? 'linear(to-r, blue.main, blue.sky)' : undefined
      }
      pb='3px'
      w='24px'
      h='24px'
      cursor='pointer'
      onClick={handleClick}
    >
      {isSubmitting && (
        <Spinner
          size='xs'
          mr={2}
        />
      )}
      {!isSubmitting && (
        <Box
          display='flex'
          alignItems='center'
          justifyContent='center'
          borderRadius='8px'
          width='20px'
          height='20px'
        >
          {!suggestionAppliquee ? (
            <Tooltip
              bg='white'
              color='blue.main'
              border='1px solid'
              borderColor='blue.border'
              borderRadius='8px'
              boxShadow='md'
              p='3'
              label={
                <VStack
                  alignItems='flex-start'
                  spacing='1'
                >
                  <Text
                    fontSize='sm'
                    fontWeight='700'
                    color='blue.main'
                  >
                    {t('cadencier.suggestion.texte', {
                      count: suggestionArticle.suggestion_quantite,
                      unite: t(
                        `unite.${suggestionArticle.unite_de_commande_client}`,
                        {count: suggestionArticle.suggestion_quantite},
                      ),
                    })}
                  </Text>
                  <Text
                    fontSize='sm'
                    color='blue.main'
                  >
                    {t('cadencier.suggestion.tooltip')}
                  </Text>
                </VStack>
              }
            >
              <span>
                <EtoilesIcon
                  color='gray.dark'
                  width='14px'
                  height='14px'
                />
              </span>
            </Tooltip>
          ) : (
            <Tooltip
              label={t('cadencier.suggestion.quantite_suggeree_presente')}
            >
              <span>
                <EtoilesIcon
                  color='white'
                  width='14px'
                  height='14px'
                />
              </span>
            </Tooltip>
          )}
        </Box>
      )}
    </HStack>
  );
};
